import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DistributionBarChart,
  MetricsCard,
  PageHeader,
  type SelectOption,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@getdevpulse/shared-ui"
import { Info } from "lucide-react"
import { observer } from "mobx-react-lite"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { SearchInput } from "../components/SearchInput"
import { getUrlParams, updateUrlParams } from "../hooks/useUrlParams"
import { formatDateLabel, getYesterday } from "../lib/dateCalculations"
import { getFromStorage, StorageKeys, saveToStorage } from "../lib/localStorage"
import {
  calculateAverageDailyPerformance,
  calculateMetrics,
  calculatePerformanceScore,
  type PerformanceScore,
} from "../lib/performanceScore"
import {
  useDataStore,
  useI18nStore,
  useMetadataStore,
  useSettingsStore,
  useStore,
  useViewStore,
} from "../stores/StoreContext"
import { routes } from "./routes"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

// Valid values for URL params
const validTimeRanges: TimeRange[] = ["7days", "14days", "28days", "8weeks", "12weeks"]
const validTabs = ["performance", "score", "prs", "staffing"]

interface TeamStats {
  teamName: string
  score: number
  prCount: number
  memberCount: number
  scoreHistory: Array<{ label: string; value: number }>
  prHistory: Array<{ label: string; value: number }>
  performanceScore: PerformanceScore
  performanceHistory: Array<{ label: string; value: number }>
}

// Pre-computed card data to avoid inline calculations during render
interface TeamCardData {
  teamName: string
  displayValue: string
  percentage: number
  trend: "up" | "down" | "neutral"
  chartData: Array<{ label: string; value: number }>
  subtitle: string
}

// Memoized team card with animation support
const TeamCard = memo(
  ({ data, onNavigate }: { data: TeamCardData; onNavigate: (teamName: string) => void }) => {
    const handleClick = () => {
      onNavigate(data.teamName)
    }

    return (
      <button
        type="button"
        className="transition-all duration-300 ease-in-out cursor-pointer w-full text-left"
        onClick={handleClick}
      >
        <MetricsCard
          title={data.teamName}
          description={data.displayValue}
          subtitle={data.subtitle}
          percentage={data.percentage}
          trend={data.trend}
          chartData={data.chartData}
          chartType="line"
          generativeAvatar={{
            name: data.teamName,
            type: "team",
          }}
        />
      </button>
    )
  },
  (prev, next) =>
    prev.data.teamName === next.data.teamName &&
    prev.data.displayValue === next.data.displayValue &&
    prev.data.percentage === next.data.percentage &&
    prev.data.trend === next.data.trend &&
    prev.data.subtitle === next.data.subtitle &&
    prev.data.chartData === next.data.chartData,
)

// Staffing data for a team
interface TeamStaffingData {
  teamName: string
  memberCount: number
  members: Array<{ id: string; handle: string; avatarUrl: string }>
}

// Helper to get GitHub avatar URL
const getGitHubAvatarUrl = (handle: string, size = 80): string =>
  `https://github.com/${handle}.png?size=${size}`

// Memoized staffing card component
const TeamStaffingCard = memo(
  ({ data, memberCountLabel }: { data: TeamStaffingData; memberCountLabel: string }) => (
    <Card className="transition-all duration-300 ease-in-out hover:shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{data.teamName}</CardTitle>
        <CardDescription>{memberCountLabel}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {data.members.map((member) => (
            <a
              key={member.id}
              href={`https://github.com/${member.handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full"
              title={member.handle}
            >
              <Avatar className="h-10 w-10 cursor-pointer">
                <AvatarImage src={member.avatarUrl} alt={member.handle} />
                <AvatarFallback className="text-xs">
                  {member.handle.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </a>
          ))}
        </div>
      </CardContent>
    </Card>
  ),
  (prev, next) =>
    prev.data.teamName === next.data.teamName &&
    prev.data.memberCount === next.data.memberCount &&
    prev.memberCountLabel === next.memberCountLabel &&
    prev.data.members.length === next.data.members.length &&
    prev.data.members.every((m, i) => m.id === next.data.members[i]?.id),
)

// Helper to get date range start based on time range
// Uses yesterday as the reference point to exclude the current incomplete day
const getDateRangeStart = (timeRange: TimeRange): Date => {
  const yesterday = getYesterday()

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // 7 days including yesterday
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // 14 days including yesterday
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // 28 days including yesterday
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (8 * 7 - 1) * 24 * 60 * 60 * 1000) // 8 weeks including yesterday
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (12 * 7 - 1) * 24 * 60 * 60 * 1000) // 12 weeks including yesterday
  }
  return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // Default to 7 days
}

// Helper to determine chart granularity and period based on available data
const getChartPeriod = (
  timeRange: TimeRange,
): { periods: number; unit: "day" | "week" | "month" } => {
  // For the new time ranges, use simple day-based periods
  if (timeRange === "7days") {
    return { periods: 7, unit: "day" }
  }
  if (timeRange === "14days") {
    return { periods: 14, unit: "day" }
  }
  if (timeRange === "28days") {
    return { periods: 28, unit: "day" }
  }
  if (timeRange === "8weeks") {
    return { periods: 8, unit: "week" }
  }
  if (timeRange === "12weeks") {
    return { periods: 12, unit: "week" }
  }
  return { periods: 7, unit: "day" } // Default
}

// Helper to get period start date
const getPeriodStart = (
  periodIndex: number,
  totalPeriods: number,
  unit: "day" | "week" | "month",
): Date => {
  const yesterday = getYesterday()
  const periodOffset = totalPeriods - periodIndex - 1

  if (unit === "day") {
    return new Date(yesterday.getTime() - periodOffset * 24 * 60 * 60 * 1000)
  }
  if (unit === "week") {
    return new Date(yesterday.getTime() - periodOffset * 7 * 24 * 60 * 60 * 1000)
  }
  // month
  const date = new Date(yesterday)
  date.setMonth(date.getMonth() - periodOffset)
  return date
}

export const TeamsPage = observer(() => {
  const store = useStore()
  const dataStore = useDataStore()
  const metadataStore = useMetadataStore()
  const i18nStore = useI18nStore()
  const settingsStore = useSettingsStore()
  const viewStore = useViewStore()

  // Initialize from localStorage first, then override with URL params
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(() => {
    const saved = getFromStorage(StorageKeys.TEAMS_TIME_RANGE) as TimeRange | null
    return saved && validTimeRanges.includes(saved) ? saved : "7days"
  })
  const [selectedTab, setSelectedTab] = useState("performance")
  const [isNormalized, setIsNormalized] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  // Navigate to team dashboard
  const navigateToTeam = useCallback(
    (teamName: string) => {
      // Get current time range to preserve it during navigation
      const currentTimeRange = viewStore.timeRange
      // Update URL params to show group view with selected team and current time range
      // Navigate to home route - query params are preserved automatically
      store.router.goTo(routes.home)
      updateUrlParams({ view: "group", team: teamName, timeRange: currentTimeRange })
    },
    [store.router, viewStore.timeRange],
  )

  // Initialize from URL params on mount (URL params take precedence over localStorage)
  useEffect(() => {
    const params = getUrlParams()

    // Set time range from URL if valid, otherwise keep localStorage value
    if (params.timeRange && validTimeRanges.includes(params.timeRange as TimeRange)) {
      setSelectedTimeRange(params.timeRange as TimeRange)
    }

    // Set tab from URL if valid
    if (params.tab && validTabs.includes(params.tab)) {
      setSelectedTab(params.tab)
    }

    // Set normalized from URL
    if (params.normalized === "true") {
      setIsNormalized(true)
    }
  }, [])

  // Handle time range change with URL and localStorage update
  const handleTimeRangeChange = useCallback((id: string) => {
    setSelectedTimeRange(id as TimeRange)
    saveToStorage(StorageKeys.TEAMS_TIME_RANGE, id)
    updateUrlParams({ timeRange: id === "30days" ? undefined : id })
  }, [])

  // Handle tab change with URL update
  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab)
    updateUrlParams({ tab: tab === "performance" ? undefined : tab })
  }, [])

  // Handle normalized toggle with URL update
  const handleNormalizedChange = useCallback((normalized: boolean) => {
    setIsNormalized(normalized)
    updateUrlParams({ normalized: normalized ? "true" : undefined })
  }, [])

  // Search handler
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query.toLowerCase())
  }, [])

  // Time range options for the dropdown (matching Dashboard)
  const timeRangeOptions: SelectOption[] = [
    { id: "7days", label: i18nStore.t("timeLast7Days") },
    { id: "14days", label: i18nStore.t("timeLast14Days") },
    { id: "28days", label: i18nStore.t("timeLast28Days") },
    { id: "8weeks", label: i18nStore.t("timeLast8Weeks") },
    { id: "12weeks", label: i18nStore.t("timeLast12Weeks") },
  ]

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    return getDateRangeStart(selectedTimeRange)
  }, [selectedTimeRange])

  // Get the timestamp for dependency tracking (Date objects don't work well in deps)
  const dateRangeTimestamp = dateRange.getTime()

  // Calculate the time range in days for performance score normalization
  const timeRangeDays = useMemo(() => {
    if (selectedTimeRange === "7days") return 7
    if (selectedTimeRange === "14days") return 14
    if (selectedTimeRange === "28days") return 28
    if (selectedTimeRange === "8weeks") return 8 * 7 // 56 days
    if (selectedTimeRange === "12weeks") return 12 * 7 // 84 days
    return 7 // Default
  }, [selectedTimeRange])

  // Determine chart period configuration
  const chartConfig = useMemo(() => {
    return getChartPeriod(selectedTimeRange)
  }, [selectedTimeRange])

  // Get teams and their members
  // Note: Using lowercase handles for case-insensitive matching to handle
  // inconsistencies in teams.json (e.g., "Sijoma" vs "sijoma")
  // Also filters based on selected teams from settings
  const teamsMap = useMemo(() => {
    const map = new Map<string, Set<string>>()
    const selectedTeams = settingsStore.selectedTeamsArray

    metadataStore.allTeams.forEach((team) => {
      // Only include teams that are selected in settings
      if (!selectedTeams.includes(team.team)) return

      if (!map.has(team.team)) {
        map.set(team.team, new Set())
      }
      // Normalize to lowercase for case-insensitive matching
      map.get(team.team)?.add(team.authorId.toLowerCase())
    })

    return map
  }, [metadataStore.allTeams, settingsStore.selectedTeamsArray])

  // Calculate team statistics with historical data
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable arrays require snapshot conversion via .slice()
  const teamStats = useMemo(() => {
    // Create a snapshot of the data to avoid MobX reactivity issues
    const commits = dataStore.allCommits.slice()
    const pullRequests = dataStore.allPullRequests.slice()
    const issues = dataStore.allIssues.slice()

    const statsMap = new Map<string, TeamStats>()
    const { periods, unit } = chartConfig

    // Initialize teams
    const initializeTeam = (teamName: string): TeamStats => {
      const history: Array<{ label: string; value: number }> = []

      for (let i = 0; i < periods; i++) {
        const periodStart = getPeriodStart(i, periods, unit)
        history.push({
          label: formatDateLabel(periodStart, unit),
          value: 0,
        })
      }

      // Initialize with empty performance score
      const emptyMetrics = calculateMetrics([], [], [])
      const emptyScore = calculatePerformanceScore(emptyMetrics, timeRangeDays)

      // Get member count for this team
      const memberCount = teamsMap.get(teamName)?.size || 0

      return {
        teamName,
        score: 0,
        prCount: 0,
        memberCount,
        scoreHistory: history.map((h) => ({ ...h })),
        prHistory: history.map((h) => ({ ...h })),
        performanceScore: emptyScore,
        performanceHistory: history.map((h) => ({ ...h })),
      }
    }

    // Helper to get period index for a date (using yesterday as reference)
    const getPeriodIndex = (date: Date): number => {
      const yesterday = getYesterday()
      let diff: number

      if (unit === "day") {
        diff = Math.floor((yesterday.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
      } else if (unit === "week") {
        diff = Math.floor((yesterday.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
      } else {
        // month
        const monthsDiff =
          (yesterday.getFullYear() - date.getFullYear()) * 12 +
          (yesterday.getMonth() - date.getMonth())
        diff = monthsDiff
      }

      const periodIndex = periods - diff - 1
      return periodIndex >= 0 && periodIndex < periods ? periodIndex : -1
    }

    // Track data per team per period for performance score calculation
    const teamDataByPeriod = new Map<
      string,
      Map<
        number,
        {
          commits: Array<{
            score: number
            insertions: number
            deletions: number
            filesChanged: number
            commitType?: string
          }>
          prs: Array<{ additions: number; deletions: number; state: string }>
          issues: Array<{ state: string }>
        }
      >
    >()

    // Initialize data structure
    const getTeamPeriodData = (teamName: string, periodIndex: number) => {
      if (!teamDataByPeriod.has(teamName)) {
        teamDataByPeriod.set(teamName, new Map())
      }
      const periodMap = teamDataByPeriod.get(teamName)
      if (!periodMap) {
        throw new Error("Period map should exist")
      }
      if (!periodMap.has(periodIndex)) {
        periodMap.set(periodIndex, { commits: [], prs: [], issues: [] })
      }
      const periodData = periodMap.get(periodIndex)
      if (!periodData) {
        throw new Error("Period data should exist")
      }
      return periodData
    }

    // Process commits for scores
    commits.forEach((commit) => {
      if (commit.timestampDate >= dateRange) {
        // Find which team this contributor belongs to
        // Use lowercase for case-insensitive matching
        for (const [teamName, members] of teamsMap.entries()) {
          if (members.has(commit.authorHandle.toLowerCase())) {
            const current = statsMap.get(teamName) || initializeTeam(teamName)
            current.score += commit.score || 0

            // Add to historical data
            const periodIndex = getPeriodIndex(commit.timestampDate)
            if (periodIndex >= 0) {
              current.scoreHistory[periodIndex].value += commit.score || 0

              // Track commit data for performance score calculation
              const periodData = getTeamPeriodData(teamName, periodIndex)
              periodData.commits.push({
                score: commit.score || 0,
                insertions: commit.insertions || 0,
                deletions: commit.deletions || 0,
                filesChanged: commit.filesChanged || 0,
                commitType: commit.commitType,
              })
            }

            statsMap.set(teamName, current)
            break
          }
        }
      }
    })

    // Process PRs for counts (merged PRs)
    pullRequests.forEach((pr) => {
      if (pr.state === "merged" && pr.mergedAtDate && pr.mergedAtDate >= dateRange) {
        // Find which team this contributor belongs to
        // Use lowercase for case-insensitive matching
        for (const [teamName, members] of teamsMap.entries()) {
          if (members.has(pr.creatorHandle.toLowerCase())) {
            const current = statsMap.get(teamName) || initializeTeam(teamName)
            current.prCount += 1

            // Add to historical data
            const periodIndex = getPeriodIndex(pr.mergedAtDate)
            if (periodIndex >= 0) {
              current.prHistory[periodIndex].value += 1

              // Track PR data for performance score calculation
              const periodData = getTeamPeriodData(teamName, periodIndex)
              periodData.prs.push({
                additions: pr.additions || 0,
                deletions: pr.deletions || 0,
                state: pr.state,
              })
            }

            statsMap.set(teamName, current)
            break
          }
        }
      }
    })

    // Process issues (all within date range)
    issues.forEach((issue) => {
      if (issue.closedAtDate && issue.closedAtDate >= dateRange) {
        const handle = issue.closedByHandle || issue.creatorHandle

        // Find which team this contributor belongs to
        // Use lowercase for case-insensitive matching
        for (const [teamName, members] of teamsMap.entries()) {
          if (members.has(handle.toLowerCase())) {
            const current = statsMap.get(teamName) || initializeTeam(teamName)

            // Add to historical data
            const periodIndex = getPeriodIndex(issue.closedAtDate)
            if (periodIndex >= 0) {
              // Track issue data for performance score calculation
              const periodData = getTeamPeriodData(teamName, periodIndex)
              periodData.issues.push({
                state: issue.state,
              })
            }

            statsMap.set(teamName, current)
            break
          }
        }
      }
    })

    // Calculate performance scores for each team
    for (const [teamName, team] of statsMap.entries()) {
      // Get all commits, PRs, and issues for this team in the selected time range
      const members = teamsMap.get(teamName)
      if (!members) continue

      const teamCommits = commits.filter((c) => members.has(c.authorHandle.toLowerCase()))
      const teamPRs = pullRequests.filter((pr) => members.has(pr.creatorHandle.toLowerCase()))
      const teamIssues = issues.filter((issue) => {
        const handle = issue.closedByHandle || issue.creatorHandle
        return members.has(handle.toLowerCase())
      })

      // Calculate average daily performance score (excluding weekends with no activity)
      const avgDailyScore = calculateAverageDailyPerformance(
        teamCommits,
        teamPRs,
        teamIssues,
        dateRange,
        new Date(),
      )

      // Create a performance score object with the calculated average
      team.performanceScore = {
        overall: avgDailyScore,
        breakdown: {
          quality: 0,
          throughput: 0,
          impact: 0,
          problemSolving: 0,
          versatility: 0,
        },
        metrics: {
          commitScore: 0,
          commitCount: 0,
          linesAdded: 0,
          linesRemoved: 0,
          filesChanged: 0,
          prCount: 0,
          prAdditions: 0,
          prDeletions: 0,
          issuesResolved: 0,
          featureCommits: 0,
          bugfixCommits: 0,
          refactoringCommits: 0,
          otherCommits: 0,
        },
      }

      // Calculate performance score history per period
      const periodMap = teamDataByPeriod.get(teamName)
      for (let i = 0; i < periods; i++) {
        const periodData = periodMap?.get(i)
        if (periodData) {
          const periodMetrics = calculateMetrics(
            periodData.commits,
            periodData.prs,
            periodData.issues,
          )
          // For historical periods, use the unit-based duration
          const periodDays = unit === "day" ? 1 : unit === "week" ? 7 : 30
          const periodScore = calculatePerformanceScore(periodMetrics, periodDays)
          team.performanceHistory[i].value = periodScore.overall
        }
      }
    }

    return Array.from(statsMap.values())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRangeTimestamp,
    timeRangeDays,
    chartConfig.periods,
    chartConfig.unit,
    teamsMap.size,
    // Force recalculation when data changes
    dataStore.allCommits.length,
    dataStore.allPullRequests.length,
    dataStore.allIssues.length,
  ])

  // Pre-compute all stats and card data for both absolute and normalized modes
  const { performanceCards, scoreCards, prCards, performanceStats, scoreStats, prStats } =
    useMemo(() => {
      if (teamStats.length === 0) {
        return {
          performanceCards: [],
          scoreCards: [],
          prCards: [],
          performanceStats: { sorted: [], average: 0, total: 0, chartData: [] },
          scoreStats: { sorted: [], average: 0, total: 0, chartData: [] },
          prStats: { sorted: [], average: 0, total: 0, chartData: [] },
        }
      }

      // Calculate averages for both absolute and normalized modes
      const totalPerformance = teamStats.reduce((sum, t) => sum + t.performanceScore.overall, 0)
      const totalScore = teamStats.reduce((sum, t) => sum + t.score, 0)
      const totalPRs = teamStats.reduce((sum, t) => sum + t.prCount, 0)
      const count = teamStats.length

      // Absolute averages
      const perfAverage = totalPerformance / count
      const scoreAverage = totalScore / count
      const prAverage = totalPRs / count

      // Normalized averages (average of per-member values, not total/avgMemberCount)
      const perfNormalizedAverage =
        teamStats.reduce((sum, t) => sum + t.performanceScore.overall / (t.memberCount || 1), 0) /
        count
      const scoreNormalizedAverage =
        teamStats.reduce((sum, t) => sum + t.score / (t.memberCount || 1), 0) / count
      const prNormalizedAverage =
        teamStats.reduce((sum, t) => sum + t.prCount / (t.memberCount || 1), 0) / count

      // Compute absolute sorting (by raw values)
      const perfSortedAbsolute = [...teamStats].sort(
        (a, b) => b.performanceScore.overall - a.performanceScore.overall,
      )
      const scoreSortedAbsolute = [...teamStats].sort((a, b) => b.score - a.score)
      const prSortedAbsolute = [...teamStats].sort((a, b) => b.prCount - a.prCount)

      // Compute normalized sorting (by value per member)
      const perfSortedNormalized = [...teamStats].sort((a, b) => {
        const aValue = a.performanceScore.overall / (a.memberCount || 1)
        const bValue = b.performanceScore.overall / (b.memberCount || 1)
        return bValue - aValue
      })
      const scoreSortedNormalized = [...teamStats].sort((a, b) => {
        const aValue = a.score / (a.memberCount || 1)
        const bValue = b.score / (b.memberCount || 1)
        return bValue - aValue
      })
      const prSortedNormalized = [...teamStats].sort((a, b) => {
        const aValue = a.prCount / (a.memberCount || 1)
        const bValue = b.prCount / (b.memberCount || 1)
        return bValue - aValue
      })

      // Create index maps for absolute and normalized orderings
      const perfAbsoluteIndexMap = new Map(
        perfSortedAbsolute.map((t, idx) => [t.teamName, idx + 1]),
      )
      const perfNormalizedIndexMap = new Map(
        perfSortedNormalized.map((t, idx) => [t.teamName, idx + 1]),
      )
      const scoreAbsoluteIndexMap = new Map(
        scoreSortedAbsolute.map((t, idx) => [t.teamName, idx + 1]),
      )
      const scoreNormalizedIndexMap = new Map(
        scoreSortedNormalized.map((t, idx) => [t.teamName, idx + 1]),
      )
      const prAbsoluteIndexMap = new Map(prSortedAbsolute.map((t, idx) => [t.teamName, idx + 1]))
      const prNormalizedIndexMap = new Map(
        prSortedNormalized.map((t, idx) => [t.teamName, idx + 1]),
      )

      // Pick the active sorting based on isNormalized
      const perfSorted = isNormalized ? perfSortedNormalized : perfSortedAbsolute
      const scoreSorted = isNormalized ? scoreSortedNormalized : scoreSortedAbsolute
      const prSorted = isNormalized ? prSortedNormalized : prSortedAbsolute

      // Helper to compute trend
      const computeTrend = (
        value: number,
        average: number,
      ): { percentage: number; trend: "up" | "down" | "neutral" } => {
        const percentDiff = average > 0 ? ((value - average) / average) * 100 : 0
        const trend: "up" | "down" | "neutral" =
          percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"
        return { percentage: Number(percentDiff.toFixed(1)), trend }
      }

      // Helper function to filter out weekends with no activity from chart data
      // Only applies to day-based periods
      const filterWeekendInactivity = (
        chartData: Array<{ label: string; value: number }>,
      ): Array<{ label: string; value: number }> => {
        if (chartConfig.unit !== "day") {
          return chartData // Only filter for day-based periods
        }

        return chartData.filter((point, index) => {
          // Calculate the date for this period
          const periodStart = getPeriodStart(index, chartConfig.periods, chartConfig.unit)
          const dayOfWeek = periodStart.getDay()
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

          // Include if it's not a weekend, or if it's a weekend with activity
          return !isWeekend || point.value > 0
        })
      }

      // Create card data for performance tab
      const perfCards: TeamCardData[] = perfSorted.map((team, index) => {
        const memberCount = team.memberCount || 1
        const displayValue = isNormalized
          ? team.performanceScore.overall / memberCount
          : team.performanceScore.overall
        const avgValue = isNormalized ? perfNormalizedAverage : perfAverage
        const { percentage, trend } = computeTrend(displayValue, avgValue)

        const currentIndex = index + 1
        const absoluteIndex = perfAbsoluteIndexMap.get(team.teamName) || 0
        const normalizedIndex = perfNormalizedIndexMap.get(team.teamName) || 0
        const alternateIndex = isNormalized ? absoluteIndex : normalizedIndex
        const subtitle = `#${currentIndex} (${isNormalized ? "Abs" : "Norm"}: #${alternateIndex})`

        // Apply weekend filtering to chart data
        const rawChartData = isNormalized
          ? team.performanceHistory.map((h) => ({ label: h.label, value: h.value / memberCount }))
          : team.performanceHistory

        return {
          teamName: team.teamName,
          displayValue: displayValue.toFixed(1),
          percentage,
          trend,
          chartData: filterWeekendInactivity(rawChartData),
          subtitle,
        }
      })

      // Create card data for score tab
      const scCards: TeamCardData[] = scoreSorted.map((team, index) => {
        const memberCount = team.memberCount || 1
        const displayValue = isNormalized ? team.score / memberCount : team.score
        const avgValue = isNormalized ? scoreNormalizedAverage : scoreAverage
        const { percentage, trend } = computeTrend(displayValue, avgValue)

        const currentIndex = index + 1
        const absoluteIndex = scoreAbsoluteIndexMap.get(team.teamName) || 0
        const normalizedIndex = scoreNormalizedIndexMap.get(team.teamName) || 0
        const alternateIndex = isNormalized ? absoluteIndex : normalizedIndex
        const subtitle = `#${currentIndex} (${isNormalized ? "Abs" : "Norm"}: #${alternateIndex})`

        // Apply weekend filtering to chart data
        const rawChartData = isNormalized
          ? team.scoreHistory.map((h) => ({ label: h.label, value: h.value / memberCount }))
          : team.scoreHistory

        return {
          teamName: team.teamName,
          displayValue: Math.round(displayValue).toLocaleString(),
          percentage,
          trend,
          chartData: filterWeekendInactivity(rawChartData),
          subtitle,
        }
      })

      // Create card data for PR tab
      const pCards: TeamCardData[] = prSorted.map((team, index) => {
        const memberCount = team.memberCount || 1
        const displayValue = isNormalized ? team.prCount / memberCount : team.prCount
        const avgValue = isNormalized ? prNormalizedAverage : prAverage
        const { percentage, trend } = computeTrend(displayValue, avgValue)

        const currentIndex = index + 1
        const absoluteIndex = prAbsoluteIndexMap.get(team.teamName) || 0
        const normalizedIndex = prNormalizedIndexMap.get(team.teamName) || 0
        const alternateIndex = isNormalized ? absoluteIndex : normalizedIndex
        const subtitle = `#${currentIndex} (${isNormalized ? "Abs" : "Norm"}: #${alternateIndex})`

        // Apply weekend filtering to chart data
        const rawChartData = isNormalized
          ? team.prHistory.map((h) => ({ label: h.label, value: h.value / memberCount }))
          : team.prHistory

        return {
          teamName: team.teamName,
          displayValue: displayValue.toFixed(1),
          percentage,
          trend,
          chartData: filterWeekendInactivity(rawChartData),
          subtitle,
        }
      })

      // Create chart data for distribution bar charts
      const perfChartData = perfSorted.map((team) => {
        const memberCount = team.memberCount || 1
        const value = isNormalized
          ? team.performanceScore.overall / memberCount
          : team.performanceScore.overall
        return { name: team.teamName, value: Number(value.toFixed(1)) }
      })

      const scoreChartData = scoreSorted.map((team) => {
        const memberCount = team.memberCount || 1
        const value = isNormalized ? team.score / memberCount : team.score
        return { name: team.teamName, value: Number(value.toFixed(0)) }
      })

      const prChartData = prSorted.map((team) => {
        const memberCount = team.memberCount || 1
        const value = isNormalized ? team.prCount / memberCount : team.prCount
        return { name: team.teamName, value: Number(value.toFixed(1)) }
      })

      return {
        performanceCards: perfCards,
        scoreCards: scCards,
        prCards: pCards,
        performanceStats: {
          sorted: perfSorted,
          average: perfAverage,
          total: totalPerformance,
          chartData: perfChartData,
        },
        scoreStats: {
          sorted: scoreSorted,
          average: scoreAverage,
          total: totalScore,
          chartData: scoreChartData,
        },
        prStats: {
          sorted: prSorted,
          average: prAverage,
          total: totalPRs,
          chartData: prChartData,
        },
      }
    }, [teamStats, isNormalized])

  // Filter cards based on search query
  const filteredPerformanceCards = useMemo(() => {
    if (!searchQuery) return performanceCards
    return performanceCards.filter((card) => card.teamName.toLowerCase().includes(searchQuery))
  }, [performanceCards, searchQuery])

  const filteredScoreCards = useMemo(() => {
    if (!searchQuery) return scoreCards
    return scoreCards.filter((card) => card.teamName.toLowerCase().includes(searchQuery))
  }, [scoreCards, searchQuery])

  const filteredPrCards = useMemo(() => {
    if (!searchQuery) return prCards
    return prCards.filter((card) => card.teamName.toLowerCase().includes(searchQuery))
  }, [prCards, searchQuery])

  // Compute staffing data (team members with avatars)
  const staffingData = useMemo(() => {
    const result: TeamStaffingData[] = []

    for (const [teamName, memberHandles] of teamsMap.entries()) {
      const members: Array<{ id: string; handle: string; avatarUrl: string }> = []

      for (const handle of memberHandles) {
        // Get the display handle from metadata store
        const displayHandle = metadataStore.getAuthor(handle) || handle
        members.push({
          id: handle,
          handle: displayHandle,
          avatarUrl: getGitHubAvatarUrl(displayHandle),
        })
      }

      // Sort members alphabetically by handle
      members.sort((a, b) => a.handle.localeCompare(b.handle))

      result.push({
        teamName,
        memberCount: members.length,
        members,
      })
    }

    // Sort by member count (biggest to smallest)
    result.sort((a, b) => b.memberCount - a.memberCount)

    return result
  }, [teamsMap, metadataStore])

  // Filter staffing data based on search query
  const filteredStaffingData = useMemo(() => {
    if (!searchQuery) return staffingData
    return staffingData.filter((team) => team.teamName.toLowerCase().includes(searchQuery))
  }, [staffingData, searchQuery])

  // Create chart data for staffing bar chart (member counts per team)
  const staffingChartData = useMemo(() => {
    return filteredStaffingData.map((team) => ({
      name: team.teamName,
      value: team.memberCount,
    }))
  }, [filteredStaffingData])

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("teamsTitle")}
          subtitle={i18nStore.t("teamsSubtitle")}
          selectOptions={timeRangeOptions}
          selectedOptionId={selectedTimeRange}
          onSelectChange={handleTimeRangeChange}
          selectPlaceholder={i18nStore.t("selectTimeRange")}
          rightContent={
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                {isNormalized ? i18nStore.t("normalizedView") : i18nStore.t("absoluteView")}
                <span
                  title={i18nStore.t("normalizedViewTooltip")}
                  className="inline-flex items-center"
                >
                  <Info
                    className="h-4 w-4"
                    style={{ cursor: "pointer" }}
                    aria-label={i18nStore.t("normalizedViewTooltip")}
                  />
                </span>
              </span>
              <Switch
                checked={isNormalized}
                onCheckedChange={handleNormalizedChange}
                aria-label={i18nStore.t("normalizedViewLabel")}
              />
            </div>
          }
        >
          <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full max-w-3xl grid-cols-4">
              <TabsTrigger value="performance">{i18nStore.t("tabPerformance")}</TabsTrigger>
              <TabsTrigger value="score">{i18nStore.t("tabScore")}</TabsTrigger>
              <TabsTrigger value="prs">{i18nStore.t("tabPRs")}</TabsTrigger>
              <TabsTrigger value="staffing">{i18nStore.t("tabStaffing")}</TabsTrigger>
            </TabsList>

            {/* Search Input */}
            <div className="mt-4">
              <SearchInput
                placeholder={i18nStore.t("searchTeams")}
                onSearch={handleSearch}
                className="max-w-md"
              />
            </div>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("performanceScoreTeamTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("performanceScoreTeamDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("performanceScoreAverage", {
                      value: performanceStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("performanceScoreComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={performanceStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="team"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredPerformanceCards.map((cardData) => (
                  <TeamCard key={cardData.teamName} data={cardData} onNavigate={navigateToTeam} />
                ))}
              </div>

              {filteredPerformanceCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {performanceStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("teamsNoData")}
                </div>
              )}
            </TabsContent>

            {/* Score Tab */}
            <TabsContent value="score" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("scoreTeamTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("scoreTeamDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("scoreAverage", {
                      value: scoreStats.average.toFixed(0),
                    })}
                  </span>{" "}
                  {i18nStore.t("scoreComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={scoreStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="team"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredScoreCards.map((cardData) => (
                  <TeamCard key={cardData.teamName} data={cardData} onNavigate={navigateToTeam} />
                ))}
              </div>

              {filteredScoreCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {scoreStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("teamsNoData")}
                </div>
              )}
            </TabsContent>

            {/* PRs Tab */}
            <TabsContent value="prs" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("prsTeamTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("prsTeamDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("prsAverage", {
                      value: prStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("prsComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={prStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="team"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredPrCards.map((cardData) => (
                  <TeamCard key={cardData.teamName} data={cardData} onNavigate={navigateToTeam} />
                ))}
              </div>

              {filteredPrCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {prStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("teamsNoData")}
                </div>
              )}
            </TabsContent>

            {/* Staffing Tab */}
            <TabsContent value="staffing" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("staffingTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("staffingDescription")}
                </p>
              </div>

              {/* Distribution bar chart showing member counts */}
              <DistributionBarChart
                data={staffingChartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="team"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredStaffingData.map((team) => (
                  <TeamStaffingCard
                    key={team.teamName}
                    data={team}
                    memberCountLabel={
                      team.memberCount === 1
                        ? i18nStore.tf("staffingMember", { count: String(team.memberCount) })
                        : i18nStore.tf("staffingMemberCount", { count: String(team.memberCount) })
                    }
                  />
                ))}
              </div>

              {filteredStaffingData.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {staffingData.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("teamsNoData")}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PageHeader>
      </div>
    </div>
  )
})

TeamsPage.displayName = "TeamsPage"
